/** biome-ignore-all lint/correctness/useExhaustiveDependencies: it's ok */
/** biome-ignore-all lint/style/noRestrictedImports: it's ok */
import NavigationTabsService from "@components/Layouts/LeftNavigationTabs/NavigationTabsService";
import ShowPublishBar from "@components/Layouts/StatusBar/Extensions/ShowPublishBar";
import StatusBar from "@components/Layouts/StatusBar/StatusBar";
import StatusBarElement from "@components/Layouts/StatusBar/StatusBarElement";
import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import IsReadOnlyHOC from "@core-ui/HigherOrderComponent/IsReadOnlyHOC";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import useWatch from "@core-ui/hooks/useWatch";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import { extractCatalogName } from "@core-ui/utils/extractCatalogName";
import styled from "@emotion/styled";
import ProtectedBranch from "@ext/catalog/actions/ProtectedBranch";
import useIsOffline from "@ext/errorHandlers/hooks/useIsOffline";
import type DefaultError from "@ext/errorHandlers/logic/DefaultError";
import BranchUpdaterService from "@ext/git/actions/Branch/BranchUpdaterService/logic/BranchUpdaterService";
import OnBranchUpdateCaller from "@ext/git/actions/Branch/BranchUpdaterService/model/OnBranchUpdateCaller";
import BranchTab from "@ext/git/actions/Branch/components/BranchTab";
import Offline from "@ext/git/actions/Offline/components/Offline";
import RepositoryBroken from "@ext/git/actions/RepositoryBroken";
import ShowRevisionsTab from "@ext/git/actions/Revisions/components/RevisionsTab/Helpers/ShowRevisionsTab";
import RevisionsCatalogTab from "@ext/git/actions/Revisions/components/RevisionsTab/RevisionsCatalogTab";
import getCommitOidFromPathname from "@ext/git/actions/Revisions/logic/utils/getCommitOidFromPathname";
import type GitBranchData from "@ext/git/core/GitBranch/model/GitBranchData";
import MergeRequestTab from "@ext/git/core/GitMergeRequest/components/MergeRequestTab";
import ShowMergeRequest from "@ext/git/core/GitMergeRequest/components/ShowMergeRequest";
import getMrStatus from "@ext/git/core/GitMergeRequest/logic/getMrStatus";
import { useMergeRequestStore } from "@ext/git/core/GitMergeRequest/logic/store/MergeRequestStore";
import type { MergeRequest } from "@ext/git/core/GitMergeRequest/model/MergeRequest";
import PublishTab from "@ext/git/core/GitPublish/PublishTab";
import t from "@ext/localization/locale/translate";
import { useIsStorageConnected } from "@ext/storage/logic/utils/useStorage";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ConnectStorage from "../../../../../extensions/catalog/actions/ConnectStorage";
import Branch from "../../../../../extensions/git/actions/Branch/components/Branch";
import Sync from "../../../../../extensions/git/actions/Sync/components/Sync";

export enum LeftNavigationTab {
	None,

	MergeRequest,
	Publish,
	Branch,
	CreateBranch,
	CatalogRevisions,

	Inbox,
	Template,
	Fragments,
	Prompt,
	FavoriteArticles,
}

const Wrapper = styled.div`
	max-height: min-content;

	::-webkit-scrollbar {
		display: none;
	}
	-ms-overflow-style: none;
	scrollbar-width: none;
`;

const ArticleStatusBar = memo(({ padding }: { padding?: string }) => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const { isNext } = usePlatform();
	const { isReadOnly } = PageDataContextService.value.conf;

	const [branch, setBranch] = useState<GitBranchData>(null);
	const [branchError, setBranchError] = useState<DefaultError>(null);
	const { mergeRequestIsDraft, mergeRequest, setMergeRequestIsDraft, setMergeRequest } = useMergeRequestStore(
		(state) => ({
			mergeRequestIsDraft: state.isDraft,
			mergeRequest: state.mergeRequest,
			setMergeRequestIsDraft: state.setIsDraft,
			setMergeRequest: state.setMergeRequest,
		}),
	);
	const mergeRequestRef = useRef<MergeRequest>(null);
	const { catalogName, repositoryError, resolvedView, isRevision } = useCatalogPropsStore(
		(state) => ({
			catalogName: extractCatalogName(state?.data?.name),
			repositoryError: state.data.repositoryError,
			resolvedView: state.data.resolvedView,
			isRevision: !!getCommitOidFromPathname(state.data.name),
		}),
		"shallow",
	);

	const apiUrlCreatorRef = useRef<ApiUrlCreator>(apiUrlCreator);
	const isStorageConnected = useIsStorageConnected();
	const isRepoError = !!repositoryError;
	const isOffline = useIsOffline();
	const { bottomTab } = NavigationTabsService.value;
	const isBranchTabOpened = bottomTab === LeftNavigationTab.Branch || bottomTab === LeftNavigationTab.CreateBranch;

	useWatch(() => {
		mergeRequestRef.current = mergeRequest;
		apiUrlCreatorRef.current = apiUrlCreator;
	}, [mergeRequest, apiUrlCreator]);

	const mergeRequestStatus = useMemo(
		() => getMrStatus(mergeRequest, mergeRequestIsDraft),
		[mergeRequest, mergeRequestIsDraft],
	);

	const setupMergeRequestState = useCallback(
		async (branch: GitBranchData, caller: OnBranchUpdateCaller) => {
			if (isNext) return;

			const response = await FetchService.fetch<MergeRequest | undefined>(
				apiUrlCreatorRef.current.getDraftMergeRequest(),
			);
			const data = response.ok ? await response.json() : null;
			const mr = data || branch?.mergeRequest;

			setMergeRequestIsDraft(!!data && !branch?.mergeRequest);
			setMergeRequest(mr ?? null);

			const mergeRequestWasBefore = !!mergeRequestRef.current;

			if (
				mr &&
				caller !== OnBranchUpdateCaller.DiscardNoReset &&
				caller !== OnBranchUpdateCaller.Publish &&
				!mergeRequestWasBefore
			)
				NavigationTabsService.setBottom(LeftNavigationTab.MergeRequest);
		},
		[isNext],
	);

	useEffect(() => {
		const onUpdateBranch = (branch: GitBranchData, caller: OnBranchUpdateCaller) => {
			void setupMergeRequestState(branch, caller);
			if (caller === OnBranchUpdateCaller.MergeRequest) return;

			setBranch(branch);
			if (!isNext && caller === OnBranchUpdateCaller.Checkout) refreshPage();
		};

		const onError = (error: DefaultError) => setBranchError(error);

		BranchUpdaterService.addListener(onUpdateBranch);
		if (!isRepoError) BranchUpdaterService.updateBranch(apiUrlCreator, OnBranchUpdateCaller.Init);
		BranchUpdaterService.addOnErrorListener(onError);

		return () => {
			BranchUpdaterService.removeListener(onUpdateBranch);
			BranchUpdaterService.removeOnErrorListener(onError);
		};
	}, [isStorageConnected, isRepoError, catalogName, isNext, setupMergeRequestState, apiUrlCreator]);

	useWatch(() => {
		BranchUpdaterService.reset();
		NavigationTabsService.setBottom(LeftNavigationTab.None);
		setMergeRequest(null);
		setMergeRequestIsDraft(false);
	}, [catalogName]);

	const bar = () => {
		if (!isStorageConnected) return [<ConnectStorage key={0} />];

		if (isRepoError) {
			return [
				<RepositoryBroken
					error={repositoryError}
					key={0}
					trigger={
						<StatusBarElement
							iconCode="cloud-alert"
							iconStyle={{ fontSize: "15px", fill: "white" }}
							tooltipText={t("git.error.broken.tooltip")}
						/>
					}
				/>,
			];
		}

		return [
			isOffline && <Offline key={3} />,
			<Sync disable={isOffline} key={0} style={{ height: "100%" }} />,
			!isNext && isReadOnly && (
				<ProtectedBranch
					key={1}
					text={isRevision ? t("git.publish.error.at-revision") : t("git.publish.error.main-branch")}
				/>
			),
			<IsReadOnlyHOC key={2}>
				<ShowPublishBar
					disable={isOffline}
					isShow={bottomTab === LeftNavigationTab.Publish}
					mergeRequestStatus={mergeRequestStatus}
					onClick={() => {
						NavigationTabsService.setBottom(
							bottomTab === LeftNavigationTab.Publish
								? LeftNavigationTab.None
								: LeftNavigationTab.Publish,
						);
					}}
				/>
			</IsReadOnlyHOC>,
		];
	};

	return (
		<Wrapper key={catalogName}>
			{isStorageConnected && !isRepoError && (
				<MergeRequestTab
					isDraft={mergeRequestIsDraft}
					mergeRequest={mergeRequest}
					setShow={(show) =>
						NavigationTabsService.setBottom(show ? LeftNavigationTab.MergeRequest : LeftNavigationTab.None)
					}
					show={bottomTab === LeftNavigationTab.MergeRequest}
				/>
			)}
			<BranchTab
				branch={branch}
				initNewBranch={bottomTab === LeftNavigationTab.CreateBranch}
				onClose={() => NavigationTabsService.setBottom(LeftNavigationTab.None)}
				onMergeRequestCreate={() => setupMergeRequestState(branch, OnBranchUpdateCaller.MergeRequest)}
				setShow={(show) =>
					NavigationTabsService.setBottom(show ? LeftNavigationTab.Branch : LeftNavigationTab.None)
				}
				show={isBranchTabOpened}
			/>
			{!isNext && (
				<>
					<RevisionsCatalogTab
						setShow={(show) =>
							NavigationTabsService.setBottom(
								show ? LeftNavigationTab.CatalogRevisions : LeftNavigationTab.None,
							)
						}
						show={bottomTab === LeftNavigationTab.CatalogRevisions}
					/>
					<PublishTab
						setShow={(show) => {
							NavigationTabsService.setBottom(show ? LeftNavigationTab.Publish : LeftNavigationTab.None);
						}}
						show={bottomTab === LeftNavigationTab.Publish}
					/>
				</>
			)}
			<StatusBar
				isOffline={isOffline}
				leftElements={
					!isRepoError && isStorageConnected && !branchError && !resolvedView
						? [
								<Branch
									branch={branch}
									disable={isOffline}
									key={LeftNavigationTab.Branch}
									onClick={() => {
										const tab = isBranchTabOpened
											? LeftNavigationTab.None
											: LeftNavigationTab.Branch;

										NavigationTabsService.setBottom(tab);
									}}
									show={isBranchTabOpened}
								/>,
								<ShowMergeRequest
									isShow={bottomTab === LeftNavigationTab.MergeRequest}
									key={LeftNavigationTab.MergeRequest}
									mergeRequest={mergeRequest}
									setShow={() => {
										const tab =
											bottomTab === LeftNavigationTab.MergeRequest
												? LeftNavigationTab.None
												: LeftNavigationTab.MergeRequest;

										NavigationTabsService.setBottom(tab);
									}}
								/>,
								<ShowRevisionsTab
									isShow={bottomTab === LeftNavigationTab.CatalogRevisions}
									key={LeftNavigationTab.CatalogRevisions}
									setShow={() => {
										const isRevisionTab = bottomTab === LeftNavigationTab.CatalogRevisions;
										const tab = isRevisionTab
											? LeftNavigationTab.None
											: LeftNavigationTab.CatalogRevisions;

										NavigationTabsService.setBottom(tab);
									}}
								/>,
							]
						: []
				}
				padding={padding}
				rightElements={bar()}
			/>
		</Wrapper>
	);
});

export default ArticleStatusBar;
